import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { UserButton as NativeUserButton } from 'capacitor-clerk/native';
import { UserButton as WebUserButton, useUser } from '@clerk/react';
import { Link, useNavigate } from 'react-router-dom';
import wordmark from '@/assets/wordmark.svg';
import { reportsApi, schedulingApi, inspectionsApi } from '@/api';
import { InspectionSummary } from '@/api/inspections';
import { notificationsApi, type Notification } from '@/api/notifications';
import { Report, Appointment } from '@/types';
import { SearchDropdown, SearchDropdownHandle, SearchResult } from './SearchModal';
import { NotificationsPanel } from './NotificationsPanel';

// Re-fetch the bell every minute while the panel is closed. When the panel
// is open we refetch on demand (on close, on mark-read) instead.
const NOTIFICATIONS_POLL_MS = 60_000;

// ── Search index helpers ─────────────────────────────────────────────────────

function buildIndex(
  reports: Report[],
  appointments: Appointment[],
  inspections: InspectionSummary[],
): SearchResult[] {
  const out: SearchResult[] = [];
  for (const a of appointments) {
    if (a.status === 'cancelled') continue;
    out.push({
      id: a.id,
      type: 'appointment',
      title: a.propertyAddress,
      subtitle: [
        a.clientName,
        new Date(a.scheduledDate).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        }),
      ].filter(Boolean).join(' · '),
      href: `/scheduling?open=${a.id}`,
    });
  }
  for (const i of inspections) {
    out.push({
      id: i.id,
      type: 'inspection',
      title: i.propertyAddress,
      subtitle: [
        i.clientName,
        new Date(i.inspectionDate).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        }),
      ].filter(Boolean).join(' · '),
      href: `/inspections/${i.id}`,
    });
  }
  for (const r of reports) {
    out.push({
      id: r.id,
      type: 'report',
      title: r.propertyAddress,
      subtitle: [
        r.clientName,
        r.inspectionDate
          ? new Date(r.inspectionDate).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            })
          : null,
      ].filter(Boolean).join(' · '),
      href: `/reports/${r.id}`,
    });
  }
  return out;
}

function filterIndex(index: SearchResult[], q: string): SearchResult[] {
  const lq = q.toLowerCase().trim();
  return index.filter(r => `${r.title} ${r.subtitle}`.toLowerCase().includes(lq));
}

// ── Component ────────────────────────────────────────────────────────────────

export function TopBar() {
  const navigate = useNavigate();
  const { user } = useUser();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const displayName =
    fullName || user?.fullName || user?.primaryEmailAddress?.emailAddress || '';

  // ── Appointments — kept around solely for the search index now that the
  //                    bell reads from the notifications endpoint instead.
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  // ── Notifications ──────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsApi.list({ limit: 50 });
      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
    } catch {
      // non-fatal — bell is best-effort, don't surface an error toast just
      // because the user briefly lost connectivity.
    }
  }, []);

  const handleMarkRead = useCallback(async (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await notificationsApi.markRead(id);
    } catch {
      // Optimistic update stays; next poll will reconcile if the call failed.
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnreadCount(0);
    try {
      await notificationsApi.markAllRead();
    } catch {
      // Optimistic — same rationale as handleMarkRead.
    }
  }, []);

  // ── Search ─────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState<SearchResult[]>([]);
  const [reportsForSearch, setReportsForSearch] = useState<Report[]>([]);
  const [inspectionsForSearch, setInspectionsForSearch] = useState<InspectionSummary[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchActiveIdx, setSearchActiveIdx] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<SearchDropdownHandle>(null);

  const searchResults = searchQuery.trim().length >= 2
    ? filterIndex(searchIndex, searchQuery)
    : [];
  const searchOpen = searchQuery.trim().length >= 2;

  // ── Data fetching ──────────────────────────────────────────────────────────

  // Appointments — fetched on mount, used only by the search index now.
  useEffect(() => {
    schedulingApi.getAppointments()
      .then(resp => { if (resp.data) setAppointments(resp.data); })
      .catch(() => {});
  }, []);

  // Notifications — fetch on mount + poll every minute while panel is closed.
  // When the panel is open the polling pauses (the user is actively looking
  // at the list; refetching while they're reading would shuffle rows).
  useEffect(() => {
    setNotifLoading(true);
    fetchNotifications().finally(() => setNotifLoading(false));
  }, [fetchNotifications]);

  useEffect(() => {
    if (notifOpen) return; // pause polling while open
    const id = window.setInterval(fetchNotifications, NOTIFICATIONS_POLL_MS);
    return () => window.clearInterval(id);
  }, [notifOpen, fetchNotifications]);

  // Refetch when the panel opens so the inspector sees the freshest list
  // even if it's been < 60s since the last poll.
  useEffect(() => {
    if (notifOpen) void fetchNotifications();
  }, [notifOpen, fetchNotifications]);

  // Rebuild search index whenever source data changes
  useEffect(() => {
    setSearchIndex(buildIndex(reportsForSearch, appointments, inspectionsForSearch));
  }, [reportsForSearch, appointments, inspectionsForSearch]);

  // Fetch reports + inspections lazily on first search focus
  const handleSearchFocus = useCallback(async () => {
    if (reportsForSearch.length > 0 || searchLoading) return;
    setSearchLoading(true);
    try {
      const [reportsResp, inspResp] = await Promise.all([
        reportsApi.getReports(1, 100),
        inspectionsApi.list(),
      ]);
      const reports = (reportsResp.data as unknown as { data: Report[] })?.data ?? [];
      setReportsForSearch(reports);
      if (inspResp.data) setInspectionsForSearch(inspResp.data);
    } catch {
      // non-fatal
    } finally {
      setSearchLoading(false);
    }
  }, [reportsForSearch.length, searchLoading]);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────

  // ⌘K / Ctrl+K focuses the search input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close search on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const returnToInput = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchQuery('');
      searchInputRef.current?.blur();
      return;
    }
    if (!searchOpen || searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIdx = Math.min(searchActiveIdx + 1, searchResults.length - 1);
      setSearchActiveIdx(nextIdx);
      dropdownRef.current?.focusItem(nextIdx);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIdx = Math.max(searchActiveIdx - 1, 0);
      setSearchActiveIdx(prevIdx);
      dropdownRef.current?.focusItem(prevIdx);
    } else if (e.key === 'Enter' && searchResults[searchActiveIdx]) {
      navigate(searchResults[searchActiveIdx].href);
      setSearchQuery('');
      searchInputRef.current?.blur();
    }
  };

  const handleSelectResult = useCallback((result: SearchResult) => {
    navigate(result.href);
    setSearchQuery('');
    searchInputRef.current?.blur();
  }, [navigate]);

  // Reset active index on query change
  useEffect(() => { setSearchActiveIdx(0); }, [searchQuery]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <header className="iw-topbar">
      <div className="iw-topbar-inner">
        <Link to="/inspections" className="iw-brand" aria-label="Flowspect home">
          <img src={wordmark} alt="Flowspect" className="iw-wordmark-img" />
        </Link>

        {/* Search — real input, dropdown results below */}
        <div
          ref={searchContainerRef}
          className="iw-search relative"
          style={{ maxWidth: 520 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onFocus={handleSearchFocus}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search inspections, appointments, reports…"
            aria-label="Search"
            autoComplete="off"
          />
          {!searchOpen && (
            <span className="iw-kbd">
              {navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K'}
            </span>
          )}

          {searchOpen && (
            <SearchDropdown
              ref={dropdownRef}
              query={searchQuery}
              results={searchResults}
              loading={searchLoading}
              activeIndex={searchActiveIdx}
              onSelect={handleSelectResult}
              onHover={setSearchActiveIdx}
              onReturnToInput={returnToInput}
            />
          )}
        </div>

        <div className="iw-user">
          {/* Bell — container stopPropagation prevents the panel's outside-click
              listener from seeing bell clicks, so toggle works correctly.
              Hidden < 768px (matches sidebar/search) since the destinations
              the panel routes to aren't usable on mobile yet. */}
          <div
            className="iw-notif relative"
            onMouseDown={e => e.stopPropagation()}
          >
            <button
              type="button"
              className="iw-icon-btn"
              aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
              onClick={() => setNotifOpen(v => !v)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
              {unreadCount > 0 && <span className="iw-dot" aria-hidden="true" />}
            </button>

            {notifOpen && (
              <NotificationsPanel
                notifications={notifications}
                loading={notifLoading}
                unreadCount={unreadCount}
                onClose={() => setNotifOpen(false)}
                onMarkRead={handleMarkRead}
                onMarkAllRead={handleMarkAllRead}
              />
            )}
          </div>

          <div className="iw-user-chip">
            {Capacitor.isNativePlatform() ? (
              <NativeUserButton style={{ width: 28, height: 28, borderRadius: '50%' }} />
            ) : (
              <WebUserButton />
            )}
            {displayName && <span className="iw-user-chip-name">{displayName}</span>}
          </div>
        </div>
      </div>
    </header>
  );
}
